// 28 questions across 6 sections. Each option's `value` matches backend SCORING keys.

export const SECTIONS = [
  {
    id: "business_profile",
    title: "Business Profile",
    overline: "Section 01",
    description: "A few quick facts so we calibrate every recommendation to your business.",
    questions: [
      {
        id: "business_name", type: "text", label: "Company name",
        placeholder: "e.g. Peak Comfort HVAC", required: true, profileField: true,
      },
      {
        id: "industry", type: "select", label: "Industry", required: true, profileField: true,
        options: [
          ["Roofing","Roofing"],["HVAC","HVAC"],["Plumbing","Plumbing"],["Electrical","Electrical"],
          ["Landscaping","Landscaping"],["Fencing","Fencing"],["General Contracting","General Contracting"],
          ["Pest Control","Pest Control"],["Cleaning Services","Cleaning Services"],
          ["Auto Repair","Auto Repair"],["Med Spa / Aesthetics","Med Spa / Aesthetics"],
          ["Dental / Healthcare","Dental / Healthcare"],["Professional Services","Professional Services"],
          ["Retail","Retail"],["Restaurant / Food","Restaurant / Food"],["Other","Other"],
        ],
      },
      {
        id: "annual_revenue", type: "radio", label: "Annual revenue range", required: true, profileField: true,
        options: [
          ["Under $250K","Under $250K"],["$250K–$1M","$250K–$1M"],["$1M–$3M","$1M–$3M"],
          ["$3M–$10M","$3M–$10M"],["$10M+","$10M+"],
        ],
      },
      {
        id: "employee_count", type: "radio", label: "Number of employees", required: true, profileField: true,
        options: [
          ["1 (just me)","1 (just me)"],["2–5","2–5"],["6–15","6–15"],["16–50","16–50"],["50+","50+"],
        ],
      },
      {
        id: "avg_transaction_value", type: "radio", label: "Average transaction / ticket value", required: true, profileField: true,
        options: [
          ["Under $250","Under $250"],["$250–$1K","$250–$1K"],["$1K–$5K","$1K–$5K"],
          ["$5K–$25K","$5K–$25K"],["$25K+","$25K+"],
        ],
      },
      {
        id: "website", type: "text", label: "Company website (optional)", placeholder: "https://",
        profileField: true,
      },
      {
        id: "google_business_url", type: "text", label: "Google Business Profile URL (optional)", placeholder: "https://g.page/...",
        profileField: true,
      },
      {
        id: "owner_involvement", type: "radio", label: "How involved is the owner in daily operations?", required: true,
        options: [
          ["100","Owner does everything"],["75","Owner heavily involved daily"],
          ["50","Owner involved several days/week"],["25","Owner reviews weekly"],["0","Owner is hands-off, runs as an asset"],
        ],
      },
    ],
  },
  {
    id: "lead_management",
    title: "Lead Management",
    overline: "Section 02",
    description: "How leads enter the business and what happens in the first 5 minutes.",
    questions: [
      { id: "leads_per_month", type: "radio", label: "Approximately how many leads do you receive per month?", required: true,
        options: [["<10","Fewer than 10"],["10-50","10–50"],["51-150","51–150"],["151-500","151–500"],["500+","500+"]] },
      { id: "has_crm", type: "radio", label: "What system tracks your leads today?", required: true,
        options: [["none","Nothing formal"],["spreadsheet","Spreadsheet / notes"],["basic","Basic CRM (e.g. Pipedrive, Zoho)"],["modern","Modern CRM (HubSpot, GoHighLevel)"],["enterprise","Industry-specific FSM (ServiceTitan, Jobber, Housecall Pro)"]] },
      { id: "lead_response_time", type: "radio", label: "How quickly does a new lead get a first response?", required: true,
        options: [["<5m","Under 5 minutes"],["5-15m","5–15 minutes"],["15-60m","15–60 minutes"],["1-4h","1–4 hours"],[">4h","More than 4 hours"],[">24h","Next business day or later"]] },
      { id: "lead_responder", type: "radio", label: "Who responds to inbound leads first?", required: true,
        options: [["owner","The owner"],["single_employee","A single employee"],["team","A small team / call center"],["automated","Automated text/email + human handoff"]] },
    ],
  },
  {
    id: "sales",
    title: "Sales",
    overline: "Section 03",
    description: "Proposals, follow-up, and the close.",
    questions: [
      { id: "proposal_process", type: "radio", label: "How are estimates / proposals created and delivered?", required: true,
        options: [["manual","Handwritten or rebuilt from scratch each time"],["template_doc","Word/PDF template, manually filled"],["software","Proposal software (Jobber, ServiceTitan, PandaDoc)"],["automated","Auto-generated from intake + e-signature"]] },
      { id: "follow_up_process", type: "radio", label: "What's your follow-up process after a proposal is sent?", required: true,
        options: [["none","None — we wait for them"],["ad_hoc","Ad-hoc — when we remember"],["manual_reminder","Manual reminders in calendar"],["sequenced","Defined multi-touch sequence (manual)"],["automated","Automated email + SMS sequence"]] },
      { id: "close_rate", type: "radio", label: "Approximate close rate on qualified proposals", required: true,
        options: [["<10","Less than 10%"],["10-25","10–25%"],["25-40","25–40%"],["40-60","40–60%"],["60+","60%+"]] },
      { id: "pipeline_visibility", type: "radio", label: "How do you see what's in the pipeline right now?", required: true,
        options: [["none","Gut feel"],["spreadsheet","A spreadsheet"],["crm_basic","CRM stages, manually updated"],["crm_dashboard","Live dashboard, automatic stage movement"]] },
    ],
  },
  {
    id: "operations",
    title: "Operations",
    overline: "Section 04",
    description: "Scheduling, dispatching, and how work actually gets done.",
    questions: [
      { id: "scheduling_method", type: "radio", label: "How are jobs scheduled and dispatched?", required: true,
        options: [["paper","Paper calendar / whiteboard"],["phone_text","Phone calls and text messages"],["calendar","Shared Google / Outlook calendar"],["spreadsheet","Spreadsheet"],["software","FSM software (Jobber, Housecall Pro, ServiceTitan)"],["integrated","Integrated — auto-routing & customer SMS"]] },
      { id: "internal_comms", type: "radio", label: "Primary internal communication tool", required: true,
        options: [["phone_text","Phone calls and personal text"],["email","Email"],["slack_teams","Slack / Microsoft Teams"],["integrated_platform","Inside the ops platform itself"]] },
      { id: "job_management", type: "radio", label: "How do crews / techs receive and complete jobs?", required: true,
        options: [["manual","Paper work orders"],["spreadsheet","Spreadsheet or messages"],["basic_software","Basic job software"],["fsm_platform","Mobile FSM app with photos, signatures, payment capture"]] },
      { id: "ops_owner_hours", type: "radio", label: "Hours per week the owner spends on operations (scheduling, dispatch, fires)", required: true,
        options: [["40+","40+ hours"],["20-40","20–40 hours"],["10-20","10–20 hours"],["5-10","5–10 hours"],["<5","Under 5 hours"]] },
    ],
  },
  {
    id: "marketing",
    title: "Marketing",
    overline: "Section 05",
    description: "Reviews, lists, and the assets that compound.",
    questions: [
      { id: "google_reviews", type: "radio", label: "Total Google reviews", required: true,
        options: [["<10","Fewer than 10"],["10-50","10–50"],["50-150","50–150"],["150-500","150–500"],["500+","500+"]] },
      { id: "email_list", type: "radio", label: "Size of your customer email list", required: true,
        options: [["none","We don't have one"],["<500","Under 500"],["500-2500","500–2,500"],["2500-10k","2,500–10,000"],["10k+","10,000+"]] },
      { id: "text_marketing", type: "radio", label: "Text (SMS) marketing capability", required: true,
        options: [["none","None"],["manual","Manual one-offs"],["automated","Automated SMS campaigns / sequences"],["advanced","Advanced — segmented + triggered by behavior"]] },
      { id: "social_activity", type: "radio", label: "Social media activity", required: true,
        options: [["none","Inactive"],["occasional","Occasional posts"],["weekly","Weekly cadence"],["daily","Daily cadence"],["managed","Professionally managed with ads"]] },
      { id: "reactivation", type: "radio", label: "Past-customer reactivation campaigns", required: true,
        options: [["never","Never"],["ad_hoc","Ad-hoc"],["yearly","Yearly"],["quarterly","Quarterly"],["automated","Automated by service-anniversary"]] },
    ],
  },
  {
    id: "administration",
    title: "Administration",
    overline: "Section 06",
    description: "The back-office. Often the biggest automation upside.",
    questions: [
      { id: "accounting_system", type: "radio", label: "Accounting / bookkeeping system", required: true,
        options: [["paper","Paper / shoebox"],["spreadsheet","Spreadsheet"],["quickbooks_desktop","QuickBooks Desktop or older"],["cloud_accounting","Cloud accounting (QBO, Xero)"],["fully_integrated","Fully integrated with ops & payments"]] },
      { id: "payroll", type: "radio", label: "Payroll system", required: true,
        options: [["manual","Manual / cash / paper checks"],["outsourced_basic","Outsourced bookkeeper does it"],["modern_software","Modern payroll software (Gusto, ADP Run)"],["fully_automated","Fully automated with time-tracking integration"]] },
      { id: "invoicing", type: "radio", label: "Invoicing process", required: true,
        options: [["manual","Manual — Word/PDF or by hand"],["template","Template + email"],["software","Invoicing software with online payment"],["automated","Auto-generated on job completion + reminders"]] },
      { id: "admin_hours_week", type: "radio", label: "Hours per week spent on admin (invoicing, AR, paperwork)", required: true,
        options: [[">20","More than 20"],["10-20","10–20"],["5-10","5–10"],["2-5","2–5"],["<2","Under 2"]] },
    ],
  },
];

export const ALL_QUESTIONS = SECTIONS.flatMap((s) => s.questions);
export const TOTAL_QUESTIONS = ALL_QUESTIONS.length;
